import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { PLANS } from '../config/plans';
import { logger } from '../utils/logger';

export function useSubscription(firebaseUser, teamOwnerId) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const ownerId = teamOwnerId || firebaseUser?.uid;

  // Initial fetch via Cloud Function
  useEffect(() => {
    if (!firebaseUser) {
      setLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        const getStatus = httpsCallable(getFunctions(), 'getSubscriptionStatus');
        const result = await getStatus();
        setSubscription(result.data);
      } catch (err) {
        logger.error('Error fetching subscription status:', err);
        setSubscription({ plan: 'starter', status: 'active' });
      }
      setLoading(false);
    };

    fetchStatus();
  }, [firebaseUser]);

  // Real-time listener for subscription changes (webhook updates while user is active)
  useEffect(() => {
    if (!ownerId) return;

    const q = query(
      collection(db, 'subscriptions'),
      where('ownerId', '==', ownerId),
      where('status', 'in', ['active', 'trialing', 'past_due'])
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          const sub = snapshot.docs[0].data();
          setSubscription({
            plan: sub.plan,
            status: sub.status,
            billingCycle: sub.billingCycle,
            currentPeriodEnd: sub.currentPeriodEnd?.toDate?.()?.toISOString() || null,
            cancelAtPeriodEnd: sub.cancelAtPeriodEnd || false,
            trialEnd: sub.trialEnd?.toDate?.()?.toISOString() || null,
          });
        }
      },
      (err) => {
        logger.error('Subscription listener error:', err);
      }
    );

    return unsubscribe;
  }, [ownerId]);

  const currentPlan = useMemo(() => {
    const planKey = subscription?.plan || 'starter';
    return PLANS[planKey] || PLANS.starter;
  }, [subscription]);

  const canUseFeature = useCallback(
    (featureName) => {
      return currentPlan.features[featureName] === true;
    },
    [currentPlan]
  );

  const isWithinLimit = useCallback(
    (limitName, currentCount) => {
      const limit = currentPlan[limitName];
      if (limit === Infinity) return true;
      return currentCount < limit;
    },
    [currentPlan]
  );

  return {
    subscription,
    subscriptionLoading: loading,
    currentPlan,
    planName: subscription?.plan || 'starter',
    isPro: subscription?.plan === 'pro' || subscription?.plan === 'business',
    isBusiness: subscription?.plan === 'business',
    isTrialing: subscription?.status === 'trialing',
    isPastDue: subscription?.status === 'past_due',
    canUseFeature,
    isWithinLimit,
  };
}
